import { useEffect, useMemo, useState } from 'react';
import { PageContext } from '@/interfaces/types/PageContext';
import { ChannelGuideData } from '@/services/contentful/contentTypes/ChannelGuide';
import {
    sortChannels,
    categoryMap,
    XtraChannels,
    groupChannelIntoCategories,
    getChannelGuideTaxonomy,
    buildChannelNavigationData,
} from '@/components-2.0/contentful/ChannelGuide/utils';
import { SortedChannels, ChannelFeedChannel } from '@/components-2.0/contentful/ChannelGuide/interfaces';

interface UseChannelGuideDataParams {
    asset: ChannelGuideData;
    pageContext?: PageContext;
    onFetchStart?: () => void;
}

export function useChannelGuideData({ asset, pageContext, onFetchStart }: UseChannelGuideDataParams) {
    const allPackages = asset.fields.allPackages;
    const initialChannelGuideData = asset.fields.initialChannelGuideLoad;
    const taxonomy = useMemo(() => getChannelGuideTaxonomy(asset.fields), [asset.fields]);
    const groupedChannels = initialChannelGuideData
        ? groupChannelIntoCategories(initialChannelGuideData, XtraChannels, categoryMap, taxonomy)
        : null;

    const [packageId, setPackageId] = useState('');
    const [channels, setChannels] = useState<SortedChannels | null>(groupedChannels || null);
    const [allChannelData, setAllChannelData] = useState<ChannelFeedChannel[] | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        async function fetchChannels() {
            let url: string = `/v2/channelfeed/${packageId}`;
            if (!asset.fields.display_upgraded_channels) {
                url = `/v2/channelfeed/package/${packageId}`;
            }
            const response = await fetch(url, { signal });
            const json = await response.json();

            if (json.errorCode) {
                const { groupedChannels, sortedByChannelNumber } = sortChannels(
                    {
                        channels: [],
                        packageDisplayName: '',
                        packageTitle: '',
                        radioType: pageContext?.platform || '',
                    },
                    XtraChannels,
                    categoryMap,
                    taxonomy,
                );
                setChannels(groupedChannels);
                setAllChannelData(sortedByChannelNumber);
                setLoading(false);
            } else {
                const { groupedChannels, sortedByChannelNumber } = sortChannels(
                    json,
                    XtraChannels,
                    categoryMap,
                    taxonomy,
                );
                setChannels(groupedChannels);
                setAllChannelData(sortedByChannelNumber);
                setLoading(false);
            }
        }

        if (packageId !== '') {
            if (!loading) {
                setLoading(true);
                onFetchStart?.();
            }
            fetchChannels();
        }

        return () => controller.abort();

        // Not including `loading` to stop pseudo-infinite fetching loop:
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageId, asset.fields.display_upgraded_channels, pageContext?.platform, taxonomy]);

    const { channelButtons, channelGuideBody } = buildChannelNavigationData(channels, taxonomy);

    function getPackageById(packageId: string) {
        for (const platformPackage of allPackages) {
            if (platformPackage.fields.sms_package_id === packageId) {
                return platformPackage;
            }
        }
    }

    function handleChannelSelection(component: string, genre: string) {
        if (component === 'All') {
            return allChannelData || initialChannelGuideData;
        } else {
            return channelGuideBody
                ? channels?.[channelGuideBody[component as keyof typeof channelGuideBody]]?.[genre]
                : null;
        }
    }

    return {
        packageId,
        setPackageId,
        channels,
        allChannelData,
        loading,
        taxonomy,
        channelButtons,
        channelGuideBody,
        getPackageById,
        handleChannelSelection,
    };
}
